import type { MemoryItem, TranscriptSummary } from '@repo/common';
import type { Section, SectionWithMetadata, SectionConfig } from '@repo/common/types/FilterSet';
import type { MemoryItemType } from '@repo/common/types/Memory';
import { formatFullDate, formatTime } from '@repo/toolslib/src/utils/component.util';
import {
  getTranscriptDate,
  getTranscriptName,
  isTranscriptFull
} from '@repo/toolslib/src/utils/transcript.util';
import type { Readable } from 'svelte/store';
import { isTranscriptDeleted } from '../transcripts';
import { FilteredType } from './base';

// Union type to support both summary and full tr
// Helper function to check if item is a TranscriptSummary
function isTranscriptSummary(item: MemoryItem): item is TranscriptSummary {
  return item._type === 'transcriptSummary';
}

// Cache for parsed dates to avoid repeated parsing
const dateCache = new WeakMap<MemoryItem, Date>();

// Helper function to get date from either type (with caching)
function getMemoryItemDate(item: MemoryItem): Date {
  // Check cache first
  const cached = dateCache.get(item);
  if (cached) {
    return cached;
  }

  let date: Date;
  if (isTranscriptSummary(item)) {
    date = item.date || item.createdAt || new Date();
  } else {
    date = getTranscriptDate(item);
  }

  // Cache the result
  dateCache.set(item, date);
  return date;
}

// Helper function to get name from either type
function getMemoryItemName(item: MemoryItem): string {
  if (isTranscriptSummary(item)) {
    return item.editedName || item.name || 'Untitled';
  } else {
    return getTranscriptName(item) || 'Untitled';
  }
}

// Concrete implementation for Memory (supports both TranscriptSummary and full Transcript)
export class FilteredMemory extends FilteredType<MemoryItem, MemoryItemType> {
  // Cache for sorted items to avoid re-sorting when data hasn't changed
  private lastItems: MemoryItem[] | null = null;
  private lastSortedItems: MemoryItem[] | null = null;
  private lastItemsLength: number = 0;

  constructor(items: Readable<MemoryItem[]>, initialLimit: number = 50) {
    super(items, initialLimit);

    // Override the default datePreset filter - memories should show all items by default
    // The base constructor sets this to 'future' which is only appropriate for calendar events
    this.filterDatePresetValues.set([]);

    // Initialize section states with defaults
    this.sectionStates.set(new Map([
      ['future', { collapsed: false, displayLimit: 10 }],
      ['today', { collapsed: false, displayLimit: 10 }],
      ['this-week', { collapsed: false, displayLimit: 10 }],
      ['this-month', { collapsed: false, displayLimit: 10 }],
      ['past-90-days', { collapsed: false, displayLimit: 10 }],
      ['past-180-days', { collapsed: false, displayLimit: 10 }],
      ['older', { collapsed: false, displayLimit: 10 }]
    ]));
  }

  protected sortItems(items: MemoryItem[]): MemoryItem[] {
    // Check if we can use cached sorted items (reference equality and length check)
    if (this.lastItems === items && this.lastSortedItems && this.lastItemsLength === items.length) {
      return this.lastSortedItems;
    }

    // Clear cache if items array changed
    if (this.lastItems !== items) {
      // When items array changes, clear date cache for old items that might no longer exist
      if (this.lastItems) {
        for (const oldItem of this.lastItems) {
          dateCache.delete(oldItem);
        }
      }
    }

    // Pre-parse all dates once with caching to avoid redundant parsing during sort
    const itemsWithDates = items.map((item) => ({
      item,
      dateTime: getMemoryItemDate(item).getTime()
    }));

    // Sort using pre-computed timestamps (much faster than repeated Date parsing)
    const sortedWithDates = itemsWithDates.sort((a, b) => b.dateTime - a.dateTime);
    const sorted = sortedWithDates.map(({ item }) => item);

    // Cache the result
    this.lastItems = items;
    this.lastSortedItems = sorted;
    this.lastItemsLength = items.length;

    return sorted;
  }

  protected mapItem(item: MemoryItem): MemoryItemType {
    // Cache the date calculation since it's used multiple times
    const date = getMemoryItemDate(item);
    // Use denormalized summary field for performance, fallback to meeting.minutes.summary for full transcripts
    const summary =
      item.summary || (isTranscriptFull(item) ? item.meeting?.minutes?.summary || '' : '');

    return {
      id: item.id,
      title: getMemoryItemName(item),
      time: formatTime(date || new Date()),
      fullDate: formatFullDate(date || new Date()),
      summary,
      workspaceId: item.workspaceId || undefined,
      isDeleting: (item as any).isDeleting || false,
      isFavorite: item.isFavorite
    };
  }

  protected generateSections(items: MemoryItemType[]): Section<MemoryItemType>[] {
    const sections: { [key: string]: MemoryItemType[] } = {};

    // Pre-calculate the current date for efficient comparison
    const now = new Date();
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const nowTime = nowOnly.getTime();

    for (const item of items) {
      // Use optimized time section calculation
      const section = this.getTimeSectionOptimized(item.fullDate, nowTime);
      if (!sections[section]) {
        sections[section] = [];
      }
      sections[section].push(item);
    }

    const sectionOrder = [
      'Future',
      'Today',
      'This Week',
      'This Month',
      'Past 90 Days',
      'Past 180 Days',
      'Older'
    ] as const;

    return sectionOrder
      .filter((section): section is (typeof sectionOrder)[number] => sections[section]?.length > 0)
      .map((title) => ({
        title,
        items: sections[title]
      }));
  }

  // Override to provide section metadata
  protected generateSectionsWithMetadata(items: MemoryItem[]): SectionWithMetadata<MemoryItem>[] {
    const sections = this.generateSectionsFromRawItems(items);

    const metadata: Record<string, SectionConfig> = {
      'Future': {
        id: 'future',
        icon: 'document_text' as any,
        iconColor: 'text-purple-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'Today': {
        id: 'today',
        icon: 'document_text' as any,
        iconColor: 'text-blue-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'This Week': {
        id: 'this-week',
        icon: 'document_text' as any,
        iconColor: 'text-green-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'This Month': {
        id: 'this-month',
        icon: 'document_text' as any,
        iconColor: 'text-yellow-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'Past 90 Days': {
        id: 'past-90-days',
        icon: 'document_text' as any,
        iconColor: 'text-orange-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'Past 180 Days': {
        id: 'past-180-days',
        icon: 'document_text' as any,
        iconColor: 'text-red-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'Older': {
        id: 'older',
        icon: 'document_text' as any,
        iconColor: 'text-gray-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      }
    };

    return sections.map(section => ({
      ...section,
      config: metadata[section.title]
    }));
  }

  // New method for lazy mapping optimization - section raw items before mapping
  public generateSectionsFromRawItems(items: MemoryItem[]): Section<MemoryItem>[] {
    const sections: { [key: string]: MemoryItem[] } = {};

    // Pre-calculate the current date for efficient comparison
    const now = new Date();
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const nowTime = nowOnly.getTime();

    for (const item of items) {
      // Get date from raw item and calculate section
      const date = getMemoryItemDate(item);
      const section = this.getTimeSectionOptimizedFromDate(date, nowTime);
      if (!sections[section]) {
        sections[section] = [];
      }
      sections[section].push(item);
    }

    const sectionOrder = [
      'Future',
      'Today',
      'This Week',
      'This Month',
      'Past 90 Days',
      'Past 180 Days',
      'Older'
    ] as const;

    return sectionOrder
      .filter((section): section is (typeof sectionOrder)[number] => sections[section]?.length > 0)
      .map((title) => ({
        title,
        items: sections[title]
      }));
  }

  // Override helper methods to extract data from MemoryItem (either type)
  protected getItemTags(item: MemoryItem): string[] {
    // Extract tags from transcript summary content or metadata
    // Make this deterministic based on item properties to avoid instability
    const tags: string[] = [];

    // Add tags based on item properties (works for both types)
    if (item.status === 'transcribed') tags.push('processed');
    if (item.duration && item.duration > 1800) tags.push('long'); // > 30 minutes

    // Check attendees (works for both TranscriptSummary and TranscriptFull)
    if (isTranscriptFull(item) && item.meeting?.attendees && item.meeting.attendees.length > 2) {
      tags.push('meeting');
    }

    // Use a deterministic hash of the ID to add some variety without randomness
    const hash = item.id.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);

    if (hash % 3 === 0) tags.push('important');
    if (hash % 5 === 0) tags.push('followup');
    if (hash % 7 === 0) tags.push('urgent');

    return tags;
  }

  protected getItemSpace(item: MemoryItem): string | undefined {
    // Extract workspace/space from item (works for both types)
    if (item.workspaceId) {
      return item.workspaceId;
    }

    // Return undefined if no workspace is assigned - this allows items to show up regardless of workspace filter
    return undefined;
  }

  protected getItemText(item: MemoryItem): string {
    // Extract searchable text from item (works for both types)
    const title = getMemoryItemName(item);
    // Use denormalized summary field, fallback to meeting.minutes.summary
    const summary =
      item.summary || (isTranscriptFull(item) ? item.meeting?.minutes?.summary || '' : '');
    const meetingTitle = isTranscriptFull(item) ? item.meeting?.minutes?.title || '' : '';
    const content = (item as any).content || '';
    return `${title} ${summary} ${meetingTitle} ${content}`.trim();
  }

  protected getItemCalendar(item: MemoryItem): string | undefined {
    // Extract calendar from item (works for both types)
    if (item.eventId) {
      return item.eventId; // Use eventId as proxy for calendar
    }

    // Use a deterministic hash of the ID to assign calendar
    const hash = item.id.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);

    return hash % 2 === 0 ? 'personal' : 'work';
  }

  protected getItemMemory(item: MemoryItem): string | undefined {
    // For both types, the memory ID is the transcript ID itself
    return item.id;
  }

  protected isItemCompleted(item: MemoryItem): boolean {
    // Check if item is completed/processed (works for both types)
    return item.status === 'ready' || item.status === 'transcribed';
  }

  protected isItemDeleted(item: MemoryItem): boolean {
    return isTranscriptDeleted(item as any);
  }

  protected matchesDatePreset(item: MemoryItem, datePresetValues: string[]): boolean {
    // If no date preset filter is applied, show all items (including future events)
    if (datePresetValues.length === 0) {
      return true;
    }

    const date = getMemoryItemDate(item);
    const now = new Date();

    // Reset time to start of day for accurate day comparisons
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = nowOnly.getTime() - dateOnly.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Check if the item matches any of the specified date preset values
    return datePresetValues.some((preset) => {
      switch (preset) {
        case 'future':
          return diffDays < 0; // Future events (negative diff means date is in future)
        case 'today':
          return diffDays === 0;
        case 'this-week':
          return diffDays >= 0 && diffDays <= 7;
        case 'this-month':
          return diffDays >= 0 && diffDays <= 30;
        case 'past-90-days':
          return diffDays >= 0 && diffDays <= 90;
        case 'past-180-days':
          return diffDays >= 0 && diffDays <= 180;
        case 'older':
          return diffDays > 180;
        default:
          return true; // Unknown preset, show the item
      }
    });
  }

  // Optimized version that takes pre-computed nowTime to avoid repeated Date creation
  private getTimeSectionOptimized(fullDate: string, nowTime: number): string {
    const date = new Date(fullDate);
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffTime = nowTime - dateOnly.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Handle future dates (should be rare for memories, but possible)
    if (diffDays < 0) {
      return 'Future';
    }

    if (diffDays === 0) return 'Today';
    if (diffDays <= 7) return 'This Week';
    if (diffDays <= 30) return 'This Month';
    if (diffDays <= 90) return 'Past 90 Days';
    if (diffDays <= 180) return 'Past 180 Days';
    return 'Older';
  }

  // Optimized version that works directly with Date objects (for raw items)
  private getTimeSectionOptimizedFromDate(date: Date, nowTime: number): string {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffTime = nowTime - dateOnly.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Handle future dates (should be rare for memories, but possible)
    if (diffDays < 0) {
      return 'Future';
    }

    if (diffDays === 0) return 'Today';
    if (diffDays <= 7) return 'This Week';
    if (diffDays <= 30) return 'This Month';
    if (diffDays <= 90) return 'Past 90 Days';
    if (diffDays <= 180) return 'Past 180 Days';
    return 'Older';
  }
}
